<template>
  <b-row align-v="center">
    <b-col>
      <b-form-select :disabled="mapping.field.required" @change="updateField" :value="mapping.field" class="mb-3 mappingText">
        <b-form-select-option v-if="!mapping.field.allowMultiple" :value="mapping.field">{{ mapping.field.label }}</b-form-select-option>
        <b-form-select-option v-for="field of availableFields" :value="field" :key="field.field">{{ field.label }}</b-form-select-option>
      </b-form-select>
    </b-col>
    <b-col>
      <b-form-select @change="updateHeader" :value="mapping.header" class="mb-3 mappingText" data-test-csv-header="header-selector-input">
        <b-form-select-option :value="mapping.header" data-test-csv-header-value="header-selector-input">
          <template v-if="mapping.header">{{ mapping.header }}</template>
          <template v-else>Please select a header</template>
        </b-form-select-option>
        <b-form-select-option v-for="header of availableHeaders" :value="header" :key="header">{{ header }}</b-form-select-option>
      </b-form-select>
    </b-col>
    <b-col class="mb-3 mappingText" v-test="'preview'">
      {{ preview }}
    </b-col>
    <b-col>
      <b-form-select v-if="mapping.field.datetimeField" @change="updateDatetimeFormat" :value="mapping.datetimeFormat" class="mb-3 mappingText" data-test-time-format="selector-input">
        <b-form-select-option :value="mapping.datetimeFormat" data-test-time-value="selector-input">
          <template v-if="formatConfig.datetimeFormat">{{ formatConfig.datetimeFormat }}</template>
          <template v-else>Please select a date format</template>
        </b-form-select-option>
        <b-form-select-option v-for="datetimeFormat of getAvailableDatetimeFormats(mapping.datetimeFormat)" :value="datetimeFormat" :key="datetimeFormat">{{ datetimeFormat }}</b-form-select-option>
      </b-form-select>
      <b-form-select v-if="mapping.field.phoneNumberField" @change="updatePhoneNumberCountryCode" :value="formatConfig.phoneNumberCountryCode" class="mb-3 mappingText">
        <b-form-select-option :value="formatConfig.phoneNumberCountryCode">
          <template v-if="formatConfig.phoneNumberCountryCode">Local Format ({{ formatConfig.phoneNumberCountryCode }})</template>
          <template v-else>E.164 Format</template>
        </b-form-select-option>
        <b-form-select-option v-if="!formatConfig.phoneNumberCountryCode" :value="$infinityAuth.getProfile().getCountryCode()">Local Format ({{ $infinityAuth.getProfile().getCountryCode() }})</b-form-select-option>
        <b-form-select-option v-else :value="null">E.164 Format</b-form-select-option>
      </b-form-select>
    </b-col>
    <b-col>
      <b-form-select v-if="mapping.field.datetimeField" @change="updateTimezone" :value="formatConfig.timezone" class="mb-3 mappingText">
        <b-form-select-option :value="formatConfig.timezone">
          <template>{{ formatConfig.timezone }}</template>
        </b-form-select-option>
        <b-form-select-option v-for="timezone of getAvailableTimezones(formatConfig.timezone)" :value="timezone" :key="timezone">{{ timezone }}</b-form-select-option>
      </b-form-select>
      <div v-if="mapping.field.phoneNumberField" class="mb-3 mappingText">
        <template v-if="formatConfig.phoneNumberCountryCode">Numbers in {{formatConfig.phoneNumberCountryCode}} local format or E.164 will be accepted.</template>
        <template v-else>Only numbers in E.164 format will be accepted.</template>
      </div>
    </b-col>
    <b-col cols="1">
      <b-button class="mb-3" @click="doDelete" :disabled="mapping.field.required || mapping.field.placeholder" v-test="'deleteMatchField'"><b-icon-trash-fill /></b-button>
    </b-col>
  </b-row>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator'
import { FieldConfig, FieldMapping, FormatConfig, DatetimeFormats, Timezones } from '@/views/Mapping.vue'
import { FormSelectPlugin, BIconTrashFill } from 'bootstrap-vue'

Vue.use(FormSelectPlugin)

@Component({
  components: {
    BIconTrashFill
  }
})
export default class FieldMap extends Vue {
  @Prop({ required: true, type: Object })
  mapping!: FieldMapping

  @Prop({ required: true, type: Array })
  availableFields!: FieldConfig[]

  @Prop({ required: true, type: Array })
  availableHeaders!: string[]

  @Prop({ required: true, type: Object })
  formatConfig!: FormatConfig

  get preview () {
    if (this.$store.state.csvFile) {
      const headerIndex = this.$store.state.csvFile?.getHeaders()?.indexOf(this.mapping.header)

      if (headerIndex > -1) {
        for (const value of this.$store.state.csvFile.getRows()) {
          if (value[headerIndex]) {
            return value[headerIndex]
          }
        }
      }
    }
  }

  getAvailableDatetimeFormats (selectedDatetimeFormat: string | undefined) {
    return DatetimeFormats.filter(
      (datetimeFormat: string) => {
        return datetimeFormat !== selectedDatetimeFormat
      }
    )
  }

  getAvailableTimezones (selectedTimezone: string | null) {
    return Timezones.filter(
      (timezone: string) => {
        return timezone !== selectedTimezone
      }
    )
  }

  updateField (fieldConfig: FieldConfig) {
    Vue.set(this.mapping, 'field', fieldConfig)
  }

  updateHeader (header: string) {
    Vue.set(this.mapping, 'header', header)
  }

  updateDatetimeFormat (datetimeFormat: string) {
    Vue.set(this.mapping, 'datetimeFormat', datetimeFormat)
    Vue.set(this.formatConfig, 'datetimeFormat', datetimeFormat)
    Vue.set(this.formatConfig, 'callStartDatetimeFormat', datetimeFormat)
  }

  updatePhoneNumberCountryCode (countryCode: string) {
    Vue.set(this.formatConfig, 'phoneNumberCountryCode', countryCode)
  }

  updateTimezone (timezone: string) {
    Vue.set(this.formatConfig, 'timezone', timezone)
  }

  @Emit('delete')
  doDelete () {
    return this.mapping
  }
}
</script>

<style lang="scss">
.mappingText {
  font-size: 12px;
}
</style>
